import React, { ReactElement } from 'react'

import AlarmActions from './AlarmActions'
import ACDStateThresholds from './ACDStateThresholds'
import CallStateThresholds from './CallStateThresholds'
import AbandonedCallAlarm from './AbandonedCallAlarm'
import {
    REPORT_ALARM_CAPABILITY_ACDSTATE,
    REPORT_ALARM_CAPABILITY_CALLSTATE,
    REPORT_ALARM_CAPABILITY_LOSTCALL
} from './Constants'
// CSS imports. Commented out because of current build process.
// import './alarms.scss'

/**
 * Component properties type.
 */
type Props = {
    alarmTypesFlags: number
    isOnAvailableStateEnabled: boolean
    setIsOnAvailableStateEnabled: (enabled: boolean) => void
    onAvailableState: string
    setOnAvailableState: (newValue: string) => void
    isOnBusyStateEnabled: boolean
    setIsOnBusyStateEnabled: (enabled: boolean) => void
    onBusyState: string
    setOnBusyState: (newValue: string) => void
    isOnNAOrDNDStateEnabled: boolean
    setIsOnNAOrDNDStateEnabled: (enabled: boolean) => void
    onNAOrDNDState: string
    setOnNAOrDNDState: (newValue: string) => void
    isOnWrapupStateEnabled: boolean
    setIsOnWrapupStateEnabled: (enabled: boolean) => void
    onWrapupState: string
    setOnWrapupState: (newValue: string) => void
    isOnInbAlertingEnabled: boolean
    setIsOnInbAlertingEnabled: (enabled: boolean) => void
    onInbAlerting: string
    setOnInbAlerting: (newValue: string) => void
    isOnTalkTimeEnabled: boolean
    setIsOnTalkTimeEnabled: (enabled: boolean) => void
    onTalkTime: string
    setOnTalkTime: (newValue: string) => void
    alarmOnNewAbandoned: boolean
    setAlarmOnNewAbandoned: (checked: boolean) => void
    playSound: boolean
    setPlaySound: (checked: boolean) => void
    activeWindow: boolean
    setActiveWindow: (checked: boolean) => void
}

/**
 * Alarms settings tab of report configuration modal window.
 *
 * @param alarmTypesFlags The alarming features support flags.
 * @param isOnAvailableStateEnabled Whether the corresponding setting is enabled.
 * @param setIsOnAvailableStateEnabled Setter for whether the corresponding setting is enabled.
 * @param onAvailableState Report property.
 * @param setOnAvailableState Setter for corresponding report property.
 * @param isOnBusyStateEnabled Whether the corresponding setting is enabled.
 * @param setIsOnBusyStateEnabled Setter for whether the corresponding setting is enabled.
 * @param onBusyState Report property.
 * @param setOnBusyState Setter for corresponding report property.
 * @param isOnNAOrDNDStateEnabled Whether the corresponding setting is enabled.
 * @param setIsOnNAOrDNDStateEnabled Setter for whether the corresponding setting is enabled.
 * @param onNAOrDNDState Report property.
 * @param setOnNAOrDNDState Setter for corresponding report property.
 * @param isOnWrapupStateEnabled Whether the corresponding setting is enabled.
 * @param setIsOnWrapupStateEnabled Setter for whether the corresponding setting is enabled.
 * @param onWrapupState Report property.
 * @param setOnWrapupState Setter for corresponding report property.
 * @param isOnInbAlertingEnabled Whether the corresponding setting is enabled.
 * @param setIsOnInbAlertingEnabled Setter for whether the corresponding setting is enabled.
 * @param onInbAlerting Report property.
 * @param setOnInbAlerting Setter for corresponding report property.
 * @param isOnTalkTimeEnabled Whether the corresponding setting is enabled.
 * @param setIsOnTalkTimeEnabled Setter for whether the corresponding setting is enabled.
 * @param onTalkTime Report property.
 * @param setOnTalkTime Setter for corresponding report property.
 * @param alarmOnNewAbandoned Report property.
 * @param setAlarmOnNewAbandoned Setter for corresponding report property.
 * @param playSound Report property.
 * @param setPlaySound Setter for corresponding report property.
 * @param activeWindow Report property.
 * @param setActiveWindow Setter for corresponding report property.
 */
const AlarmsTab: React.FC<Props> = ({
    alarmTypesFlags,
    isOnAvailableStateEnabled,
    setIsOnAvailableStateEnabled,
    onAvailableState,
    setOnAvailableState,
    isOnBusyStateEnabled,
    setIsOnBusyStateEnabled,
    onBusyState,
    setOnBusyState,
    isOnNAOrDNDStateEnabled,
    setIsOnNAOrDNDStateEnabled,
    onNAOrDNDState,
    setOnNAOrDNDState,
    isOnWrapupStateEnabled,
    setIsOnWrapupStateEnabled,
    onWrapupState,
    setOnWrapupState,
    isOnInbAlertingEnabled,
    setIsOnInbAlertingEnabled,
    onInbAlerting,
    setOnInbAlerting,
    isOnTalkTimeEnabled,
    setIsOnTalkTimeEnabled,
    onTalkTime,
    setOnTalkTime,
    alarmOnNewAbandoned,
    setAlarmOnNewAbandoned,
    playSound,
    setPlaySound,
    activeWindow,
    setActiveWindow
}: Props): ReactElement => {
    const isACDStateThresholdsSectionDisplayed =
        (alarmTypesFlags & REPORT_ALARM_CAPABILITY_ACDSTATE) === REPORT_ALARM_CAPABILITY_ACDSTATE

    const isCallStateThresholdsSectionDisplayed =
        (alarmTypesFlags & REPORT_ALARM_CAPABILITY_CALLSTATE) === REPORT_ALARM_CAPABILITY_CALLSTATE

    const isAbandonedCallSectionDisplayed =
        (alarmTypesFlags & REPORT_ALARM_CAPABILITY_LOSTCALL) === REPORT_ALARM_CAPABILITY_LOSTCALL

    return (
        <div className='alarms-tab tab-area'>
            {isACDStateThresholdsSectionDisplayed && (
                <ACDStateThresholds
                    isOnAvailableStateEnabled={isOnAvailableStateEnabled}
                    setIsOnAvailableStateEnabled={setIsOnAvailableStateEnabled}
                    onAvailableState={onAvailableState}
                    setOnAvailableState={setOnAvailableState}
                    isOnBusyStateEnabled={isOnBusyStateEnabled}
                    setIsOnBusyStateEnabled={setIsOnBusyStateEnabled}
                    onBusyState={onBusyState}
                    setOnBusyState={setOnBusyState}
                    isOnNAOrDNDStateEnabled={isOnNAOrDNDStateEnabled}
                    setIsOnNAOrDNDStateEnabled={setIsOnNAOrDNDStateEnabled}
                    onNAOrDNDState={onNAOrDNDState}
                    setOnNAOrDNDState={setOnNAOrDNDState}
                    isOnWrapupStateEnabled={isOnWrapupStateEnabled}
                    setIsOnWrapupStateEnabled={setIsOnWrapupStateEnabled}
                    onWrapupState={onWrapupState}
                    setOnWrapupState={setOnWrapupState}
                />
            )}
            {isCallStateThresholdsSectionDisplayed && (
                <CallStateThresholds
                    alarmTypesFlags={alarmTypesFlags}
                    isOnInbAlertingEnabled={isOnInbAlertingEnabled}
                    setIsOnInbAlertingEnabled={setIsOnInbAlertingEnabled}
                    onInbAlerting={onInbAlerting}
                    setOnInbAlerting={setOnInbAlerting}
                    isOnTalkTimeEnabled={isOnTalkTimeEnabled}
                    setIsOnTalkTimeEnabled={setIsOnTalkTimeEnabled}
                    onTalkTime={onTalkTime}
                    setOnTalkTime={setOnTalkTime}
                />
            )}
            {isAbandonedCallSectionDisplayed && (
                <AbandonedCallAlarm
                    alarmOnNewAbandoned={alarmOnNewAbandoned}
                    setAlarmOnNewAbandoned={setAlarmOnNewAbandoned}
                />
            )}
            <AlarmActions
                playSound={playSound}
                setPlaySound={setPlaySound}
                activeWindow={activeWindow}
                setActiveWindow={setActiveWindow}
            />
        </div>
    )
}

export default AlarmsTab
